import isEqual from 'lodash/isEqual';

export default abstract class AbstractProxy<Data> {
  /**
   * Mutates the proxy object structure to match the given plain immutable DOLAN
   * object structure. All existing nested proxy instances, like ViewProxy, ComponentInstanceProxy,
   * etc. are PRESERVED, and new ones are created as necessary.
   *
   * This performs any required deep-cloning and proxy instantiations, to avoid issues with mutations
   * done later to the structure.
   *
   * @param dolan the latest state of DOLAN
   * @returns whether actual changes were applied to the state
   */
  setState(properties: Data): boolean;
  setState(properties: any): boolean {
    let changed = false;
    const instance: any = this;
    // Delete own enumerable keys not present in properties
    Object.keys(instance).forEach((key) => {
      if (!(key in properties) && typeof instance[key] !== 'undefined') {
        // eslint-disable-next-line no-param-reassign
        delete instance[key];
        changed = true;
      }
    });
    // Set new property values
    return this.assignProperties(properties) || changed;
  }

  /**
   * Mutates the proxy instance by assigning all the given values (as own enumerable
   * properties), cloning mutable values and creating nested proxy instances as needed.
   * Returns whether any of the properties were actually changed.
   */
  protected assignProperties(values: Partial<Data>): boolean {
    return Object.entries(values).reduce(
      (changed, [propertyName, rawValue]) => this.assignProperty(propertyName, rawValue) || changed,
      false,
    );
  }

  protected assignProperty(propertyName: string, newValue: any): boolean {
    const instance: any = this;
    const value = this.prepareAssignedProperty(propertyName, newValue);
    if (isEqual(value, instance[propertyName])) {
      return false;
    }
    instance[propertyName] = value;
    return true;
  }

  protected abstract prepareAssignedProperty(propertyName: string, newValue: any): unknown;
}
